import { Link } from "@inertiajs/react";
import { useMemo, useState } from "react";
import type { SalonjobsJobsProps } from "./_props";
import { fmt } from "@/lib/_format";
import { salonjobsUrl } from "@/lib/_url";
import { HeroCarousel } from "./_hero-carousel";
import { SalonsGrid } from "./_salon-card";
import {
  FilterDock,
  INITIAL_FILTERS,
  countActiveFilters,
  filterAndSort,
  SORT_LABELS,
  type FilterState,
  type SortKey,
} from "./_filter";

export default function SalonjobsJobs({
  summary = { total_jobs: 0, total_salons: 0 },
  facets = { positions: [], employment_categories: [], areas: [] },
  featured_videos = [],
  salons,
}: SalonjobsJobsProps) {
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  const safeSalons = salons ?? [];
  const filtered = useMemo(() => filterAndSort(safeSalons, filters), [safeSalons, filters]);
  const reset = () => setFilters(INITIAL_FILTERS);
  const activeCount = countActiveFilters(filters);

  return (
    <>
      <div className="min-h-screen bg-[#f5f5f7] pb-20">
        <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-30">
          <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-2 flex items-center gap-2">
            <Link href={salonjobsUrl()} className="flex items-center gap-2 min-w-0">
              <span className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">Salonjobs</span>
              <div className="hidden sm:flex flex-col leading-tight">
                <span className="text-[13px] font-bold text-slate-700">求人を探す</span>
                <span className="text-[9px] font-bold text-slate-400 tracking-wider">SALON JOBS SEARCH</span>
              </div>
            </Link>
          </div>
        </header>

        <HeroCarousel videos={featured_videos} totalJobs={summary.total_jobs} />

        <main className="max-w-[1800px] mx-auto px-3 sm:px-6 mt-4">
          <ResultsHeader
            count={filtered.length}
            total={salons.length}
            sort={filters.sort}
            onSortChange={(sort) => setFilters({ ...filters, sort })}
          />
          <SalonsGrid salons={filtered} />
        </main>

        <FilterDock
          filters={filters}
          setFilters={setFilters}
          facets={facets}
          onReset={reset}
          activeCount={activeCount}
          open={filterOpen}
          setOpen={setFilterOpen}
          resultCount={filtered.length}
        />
      </div>
    </>
  );
}

function ResultsHeader({
  count,
  total,
  sort,
  onSortChange,
}: {
  count: number;
  total: number;
  sort: SortKey;
  onSortChange: (s: SortKey) => void;
}) {
  return (
    <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
      <div>
        <span className="text-base font-bold text-slate-900 tabular-nums">{fmt(count)}</span>
        <span className="text-xs text-slate-500 ml-1">/ {fmt(total)} サロン</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">並び替え</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as SortKey)}
          className="text-xs border border-slate-300 rounded px-2 py-1.5 min-h-[34px] bg-white"
        >
          {(Object.entries(SORT_LABELS) as Array<[SortKey, string]>).map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
